import http from "./httpService";
import { apiUrl } from "config/envConfig";

const base = `${apiUrl}/payment-mgmt/admin/wallettransaction`;

// Normalize transaction object keys from various backend shapes (capitalized / camelCase)
const normalizeTransaction = (t) => {
  if (!t || typeof t !== 'object') return t;
  return {
    id: t.id ?? t.Id ?? null,
    driverId: t.driverId ?? t.DriverId ?? null,
    walletId: t.walletId ?? t.WalletId ?? null,
    driverName: t.driverName ?? t.DriverName ?? t.driver ?? null,
    amount: (t.amount ?? t.Amount) != null ? Number(t.amount ?? t.Amount) : (t.amount ?? null),
    transactionType: t.transactionType ?? t.TransactionType ?? null,
    paymentMethod: t.paymentMethod ?? t.PaymentMethod ?? null,
    status: t.status ?? t.Status ?? null,
    direction: t.direction ?? t.Direction ?? null,
    timestamp: t.timestamp ?? t.Timestamp ?? t.time ?? null,
    transactionNumber: t.transactionNumber ?? t.TransactionNumber ?? null,
    orderNumber: t.orderNumber ?? t.OrderNumber ?? t.order?.orderNumber ?? t.order?.OrderNumber ?? null,
    orderId: t.orderId ?? t.OrderId ?? t.order?.id ?? t.order?.Id ?? null,
    orderIds: t.orderIds ?? t.OrderIds ?? [],
    creationDate: t.creationDate ?? t.CreationDate ?? null,
    modificationDate: t.modificationDate ?? t.ModificationDate ?? null,
    driverFees: t.driverFees ?? t.DriverFees ?? t.driverFee ?? t.DriverFee ?? null,
    fanoosFees: t.fanoosFees ?? t.FanoosFees ?? t.fanoosFee ?? t.FanoosFee ?? null,
    transactionTypeName: t.transactionTypeName ?? t.TransactionTypeName ?? null,
    statusName: t.statusName ?? t.StatusName ?? null,
    raw: t,
  };
};

/**
 * Get all wallet transactions with filtering and pagination
 * @param {Object} filter - Filter object containing pageNumber, pageSize, driverId, type, status, from, to
 * @returns {Promise<Object>} Paginated transaction list
 */
export const getAllWalletTransactions = async (filter) => {
  const { data } = await http.post(`${base}/all`, filter);
  // Support capitalized `Data` wrapper and various shapes (items, page, list, data)
  const raw = data?.data || data?.Data || data || {};

  let items = [];
  if (Array.isArray(raw.items)) items = raw.items;
  else if (Array.isArray(raw.page)) items = raw.page;
  else if (Array.isArray(raw.list)) items = raw.list;
  else if (Array.isArray(raw.data)) items = raw.data;
  else if (Array.isArray(raw.Page)) items = raw.Page;
  else if (Array.isArray(raw)) items = raw;

  // normalize all items to consistent key names expected by UI
  const normalizedItems = (items || []).map((it) => normalizeTransaction(it));

  const totalCount = raw.totalCount || raw.totalRecords || raw.totalDisplayRecords || raw.TotalRecords || raw.TotalDisplayRecords || normalizedItems.length || 0;

  return { items: normalizedItems, totalCount };
};

/**
 * Get wallet transaction by ID
 * @param {string} id - Transaction ID
 * @returns {Promise<Object>} Transaction details
 */
export const getWalletTransactionById = async (id) => {
  const { data } = await http.get(`${base}/${id}`);
  const raw = data?.data || data?.Data || data || null;
  return normalizeTransaction(raw) || raw;
};

/**
 * Create a new wallet transaction
 * @param {Object} payload - Transaction creation payload with walletId, amount, type, status, files
 * @returns {Promise<Object>} Created transaction
 */
export const createWalletTransaction = async (payload, onUploadProgress = null) => {
  const formData = new FormData();
  // Add driverIds (preferred) or walletId as fallback
  if (payload.driverIds && Array.isArray(payload.driverIds) && payload.driverIds.length > 0) {
    payload.driverIds.forEach((id) => formData.append("DriverIds", id));
  } else if (payload.walletId) {
    formData.append("walletId", payload.walletId);
  }

  // Add all regular fields
  formData.append("amount", payload.amount);
  // ensure enum-like fields are sent as numeric strings
  formData.append("transactionType", String(Number(payload.transactionType || 0)));
  formData.append("paymentMethod", String(Number(payload.paymentMethod || 0)));
  if (payload.status !== undefined && payload.status !== null) {
    formData.append("status", String(Number(payload.status)));
  }

  // Add files if present
  if (payload.files && payload.files.length > 0) {
    payload.files.forEach((file) => {
      formData.append("files", file);
    });
  }

  const config = {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  };
  if (onUploadProgress && typeof onUploadProgress === 'function') config.onUploadProgress = onUploadProgress;

  const { data } = await http.post(`${base}/create`, formData, config);
  const raw = data?.data || data?.Data || data || null;
  return normalizeTransaction(raw) || raw;
};

/**
 * Update wallet transaction status
 * @param {string} id - Transaction ID
 * @param {number} status - New status (1=Pending, 2=Success, 3=Failed, 4=Reversed)
 * @returns {Promise<Object>} Updated transaction
 */
export const updateWalletTransactionStatus = async (id, status) => {
  // Backend expects the raw enum numeric value in the body (not an object).
  const { data } = await http.put(
    `${base}/status/${id}`,
    Number(status),
    { headers: { "Content-Type": "application/json" } }
  );
  const raw = data?.data || data?.Data || data || null;
  return normalizeTransaction(raw) || raw;
};

const walletTransactionService = {
  getAllWalletTransactions,
  getWalletTransactionById,
  createWalletTransaction,
  updateWalletTransactionStatus,
};

export default walletTransactionService;
